import {
  cloneElement,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
  type MouseEvent,
  type MouseEventHandler,
  type ReactElement,
} from 'react';

/**
 * 禁用原因的统一呈现。
 *
 * 这一节的核心是一个矛盾：
 *   需求 A：禁用控件必须能说明"为什么不能用"（悬停看到、读屏听到）。
 *   需求 B：原生 `disabled` 的按钮**收不到任何指针事件** —— 连 `mouseenter` 都收不到。
 * 两者不能同时靠原生 `disabled` 满足，所以这里选择了 `aria-disabled` + 自己拦点击：
 *
 *   1. 指针事件照常到达，悬停/聚焦/被拦下的那一次点击都能弹出原因；
 *   2. 动作由我们在 `onClick` 里主动丢弃（并 `preventDefault()`，顺带挡住表单提交）；
 *   3. 控件仍然可以被聚焦 —— 这是必须的：`aria-describedby` 指向的原因文本
 *      只有聚焦时读屏才会念出来，把控件从焦点循环里踢出去等于把原因藏起来。
 *
 * 代价写在明处：语义从"浏览器保证不可用"变成"我们保证不可用"，
 * 所以第 2 条是**行为**上的硬承诺，不能只留个 `aria-disabled` 就算完。
 *
 * 另外：如果子元素本身是原生 `disabled`（第三方控件不好改），指针事件会被浏览器吞掉，
 * 但**外层容器**不受影响 —— 悬停判断挂在外层容器上，原因照样显示得出来。
 */

/** 常见原因的固定说法，避免同一个"不可用"在界面里出现五种文案 */
export const DISABLED_REASONS = {
  disarmed: '未解锁，无法起飞',
  linkOffline: '链路离线',
  unsupportedByFrame: '该机型不支持此命令',
  noVehicle: '未选中机体',
  notInMission: '当前不在任务模式',
} as const;

/**
 * 被包装控件需要能接住的注入属性。
 *
 * 用 `MouseEventHandler`（React 自带的这个类型带"双变"处理）而不是自己写
 * `(event: MouseEvent<HTMLElement>) => void`：否则 `<button>` 上更窄的事件类型
 * 会因为函数参数的逆变而无法赋值进来，调用点被迫写类型断言。
 */
export interface DisabledChildProps {
  'aria-disabled'?: boolean | undefined;
  'aria-describedby'?: string | undefined;
  'data-disabled-reason'?: string | undefined;
  /** 禁用时会被换成"只显示原因、不执行动作"的处理函数；可用时完全不碰 */
  onClick?: MouseEventHandler<HTMLElement> | undefined;
}

export interface DisabledReasonProps {
  /** 不可用的中文原因；传 `undefined` 表示可用 —— 此时本组件完全不包装子元素 */
  reason: string | undefined;
  /** 单个可交互元素。它需要把收到的 props 透传到真正的 DOM 节点（原生元素天然满足） */
  children: ReactElement<DisabledChildProps>;
}

function isInside(target: EventTarget | null, container: HTMLElement | null): boolean {
  return container !== null && target instanceof Node && container.contains(target);
}

export function DisabledReason({ reason, children }: DisabledReasonProps): JSX.Element {
  const disabled = reason !== undefined;
  // 收窄一次：下面 JSX 里的分支拿不到 `reason !== undefined` 这个判断的收窄
  const text = reason ?? '';
  const [revealed, setRevealed] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const descriptionId = useId();

  useEffect(() => {
    if (!disabled) {
      setRevealed(false);
      return;
    }
    // 键盘用户拿不到 hover：用 focusin/focusout（它们会冒泡，能覆盖整棵子树）来显示原因，
    // 挂在 document 上是因为焦点可能在子元素之间移动，而我们只关心它是否还在容器内。
    const handleFocusIn = (event: FocusEvent): void => {
      if (isInside(event.target, wrapperRef.current)) setRevealed(true);
    };
    const handleFocusOut = (event: FocusEvent): void => {
      if (isInside(event.target, wrapperRef.current)) setRevealed(false);
    };
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [disabled]);

  const handleClick = (event: MouseEvent<Element>): void => {
    // 丢弃动作：这是"我们保证不可用"的实现本身。同时阻止 `type="submit"` 之类的默认行为。
    event.preventDefault();
    // 触摸屏没有悬停，用户唯一能拿到原因的时机就是"这一下被拦下的点击"，所以在这里显示
    setRevealed(true);
  };

  const handleMouseOver = (): void => {
    if (disabled) setRevealed(true);
  };

  const handleMouseOut = (event: MouseEvent<Element>): void => {
    // relatedTarget 在容器内说明只是从子元素之间移动，不该收起来
    if (disabled && !isInside(event.relatedTarget, wrapperRef.current)) setRevealed(false);
  };

  const content = (
    // 始终是同一个 Fragment 形状：禁用状态切换时只换第一个子元素的 props，
    // 否则 React 会换掉整棵子树（重新挂载 = 丢焦点、丢输入法状态）
    <>
      {disabled
        ? cloneElement(children, {
            'aria-disabled': true,
            'aria-describedby': descriptionId,
            'data-disabled-reason': text,
            onClick: handleClick,
          })
        : children}
      {disabled ? (
        <>
          {/*
            常驻的原因文本：视觉隐藏但一直在 DOM 里，所以读屏随时能通过 aria-describedby 读到，
            不依赖"提示有没有弹出来"这回事。
          */}
          <span id={descriptionId} data-hgc-disabled-reason-text="" style={VISUALLY_HIDDEN}>
            {text}
          </span>
          {revealed ? (
            // 可见提示对读屏是 aria-hidden：它的内容与上面那份常驻说明完全重复，
            // 不标记的话会被念两遍
            <span role="tooltip" aria-hidden="true" data-hgc-disabled-reason-tip="" style={TIP}>
              {text}
            </span>
          ) : null}
        </>
      ) : null}
    </>
  );

  return (
    <span
      ref={wrapperRef}
      data-hgc-disabled-reason={disabled ? 'true' : 'false'}
      style={WRAPPER}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      {content}
    </span>
  );
}

/** 在文档流里占位但不显示的标准写法（`display: none` 会让读屏也读不到） */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** 定位容器：提示挂在控件下方，所以容器必须是定位上下文 */
const WRAPPER: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
};

const TIP: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: '0.25rem',
  // 提示只该盖过普通布局，不该盖过浮层本身 —— 用最低一档令牌
  zIndex: 'var(--z-toast)',
  whiteSpace: 'nowrap',
  padding: '0.25rem 0.5rem',
  background: 'var(--surface-3)',
  color: 'var(--fg)',
  border: '1px solid var(--line-strong)',
  borderRadius: 'var(--radius-control, 0.5rem)',
  fontSize: '0.75rem',
};
